<template>
  <div
    :class="[
      'action-box-wrapper',
      { dashed: index === 2 },
      { droppable: dropZoneActive },
    ]"
    @dragenter.prevent="setDropzoneActive"
    @dragover.prevent="setDropzoneActive"
    @dragleave.prevent="setDropzoneInactive"
    @drop.prevent="onDrop"
  >
    <div class="action-box">
      <div class="action-box-number">{{ index }}</div>
      <img :src="require(`@/assets/images/${img}`)" class="action-box-img" />
      <h2 class="action-box-title">{{ title }}</h2>
      <p
        v-if="isUploaded || (text && !fileRef)"
        class="action-box-text"
        v-html="text"
      ></p>
      <div v-if="boxType === 'upload'">
        <div v-if="fileRef" class="file-info">
          <button class="remove-file" @click="removeFile">×</button>
          <div class="file-details-container">
            <img src="@/assets/images/xls-file.svg" alt="Icon" class="icon" />
            <div class="file-details">
              <div class="file-name">{{ fileRef.name }}</div>
              <div class="file-size">{{ formattedFileSize }}</div>
            </div>
          </div>
          <div v-if="uploading" class="file-status">
            <img src="@/assets/images/icons/button-check.svg" />
            Dosya inceleniyor...
          </div>
          <div v-else>
            <div v-if="!fileError" class="file-status">
              <img src="@/assets/images/icons/button-check.svg" />
              Dosya yüklenmeye hazır.
            </div>
            <div v-if="fileError" class="file-status error">
              <img src="@/assets/images/icons/alert-circle.svg" />
              <span class="warning-message">Yüklenemedi!</span>
              <span class="file-error-message">{{ fileError }}</span>
            </div>
          </div>
        </div>
        <input
          type="file"
          ref="fileInput"
          @change="handleFileChange"
          hidden
          accept=".xls"
        />
      </div>

      <AppButton
        v-if="boxType === 'download'"
        class="action-box-btn"
        title="Şablon İndir"
        primary
        md
        @click="downloadTemplate"
      />
      <AppButton
        v-if="boxType === 'upload' && !uploading"
        class="action-box-btn"
        :title="fileError || isUploaded ? 'Yeni Dosya Seç' : 'Dosya Seç'"
        primary
        md
        @click="fileInput.click()"
      />
      <p
        v-if="warningText && !fileRef && !isUploaded"
        class="action-box-warning"
      >
        {{ warningText }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { AppButton } from "@n11/so-ui-components";
import {
  HEADER_TAB_UPLOAD,
} from "@/utils/enum/bulkActionTabs";

const store = useStore();

const props = defineProps({
  index: {
    type: Number,
    required: true,
  },
  boxType: {
    type: String,
    required: false,
    default: "download",
  },
  img: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: false,
    default: "",
  },
  text: {
    type: String,
    required: false,
    default: "",
  },
  btnText: {
    type: String,
    required: false,
    default: "",
  },
  offerPeriodStart: {
    type: String,
    required: false,
    default: "",
  },
  offerPeriodEnd: {
    type: String,
    required: false,
    default: "",
  },
  maxFileSize: {
    type: Number,
    default: 25,
    required: false,
  },
  warningText: {
    type: String,
    required: false,
    default: "",
  },
  tab: {
    type: String,
    required: false,
    default: "CREATE",
  },
});

const sessionKey = "PRODUCT_DEAL_PAYLOAD";
const emit = defineEmits(["refreshTable"]);
const dropZoneActive = ref();
const fileRef = ref(null);
const fileInput = ref(null);
const fileError = ref(null);
const uploading = ref(false);
const isUploaded = ref(false);
const uploadType = ref("EXCEL-CREATE");
const title = ref(props.title);
const text = ref(props.text);
const img = ref(props.img);
const formattedFileSize = computed(() =>
  fileRef.value ? (fileRef.value.size / 1024 / 1024).toFixed(2) + " MB" : ""
);
const removeFile = () => {
  fileRef.value = null;
  fileError.value = null;
  uploading.value = false;
  title.value = props.title;
  text.value = props.text;
  img.value = props.img;

  const fileInput = document.querySelector('input[type="file"]');
  fileInput.value = null;
};
const setDropzoneActive = () => {
  if (props.boxType === "upload") {
    dropZoneActive.value = true;
  }
};

const setDropzoneInactive = () => {
  if (props.boxType === "upload") {
    dropZoneActive.value = false;
  }
};

const onDrop = (e) => {
  if (props.boxType === "upload") {
    setDropzoneInactive();
    handleFileDrop(e);
  }
};

const handleFileChange = (event) => {
  fileRef.value = null;

  validateFile(event.target.files[0]);
};

const handleFileDrop = (event) => {
  fileRef.value = null;
  if (!event) return false;
  event.preventDefault();

  const droppedFile = event.dataTransfer.files[0];
  validateFile(droppedFile);
};

const formatDate = (dateString) => {
  const date = new Date(dateString);

  const day = date.getDate();
  const month = date.toLocaleString("tr-TR", { month: "long" });

  return `${day} ${month}`;
};
const downloadTemplate = async () => {
  await store.dispatch("templates/dealCreateTemplate", {
    startDate: new Date().toISOString().slice(0, 19).replace("T", " "),
    finishDate: "2099-01-04 23:59:59",
  });
};

const validateFile = async (file) => {
  if (file && file.name.endsWith(".xls")) {
    if (file.size <= props.maxFileSize * 1024 * 1024) {
      fileRef.value = file;
      uploading.value = true;
      uploadType.value =
        props.tab === HEADER_TAB_UPLOAD ? "EXCEL-CREATE" : props.tab;
      try {
        const confirmation = await store.dispatch("products/confirmDealUpload", {
          file,
          offerPeriodStart: props.offerPeriodStart,
          offerPeriodEnd: props.offerPeriodEnd,
        });
        uploading.value = false;
        if (confirmation && confirmation.id) {
          fileRef.value = null;
          removeFile();

          isUploaded.value = true;
          title.value = "Dosyanı Aldık";
          text.value =
            "Dosyanız sisteme işleniyor.<br /><u>Dosya Yükleme Geçmişim</u>’den detayları kontrol edebilir,<br />yeni ürünler eklemek için farklı bir dosya yükleyebilirsiniz.";
          img.value = "bulk-uploaded@2x.png";
          emit("refreshTable", {
            tab: "file-table",
            taskId: confirmation.id,
            refresh: true,
            headerTab: props.tab,
          });
        }
      } catch (err) {
        title.value = "Dosya Yüklenemedi!";
        img.value = "bulk-not-uploaded@2x.png";
        fileError.value = err.message;
        uploading.value = false;
      }
    } else {
      fileRef.value = file;
      fileError.value = `Dosya en fazla ${props.maxFileSize} MB boyutunda olmalıdır. Düzenledikten sonra tekrar yükleyin.`;
    }
  } else {
    fileRef.value = file;
    fileError.value =
      "Dosya uzantısı .xls olmalıdır. Düzenledikten sonra tekrar yükleyin.";
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.action-box-wrapper {
  position: relative;
  background-color: $colorM05;
  border: 2px solid $colorM50;
  border-radius: 8px;
  width: 100%;

  &.dashed {
    border: none;

    &::before {
      content: "";
      background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23FF44EE' stroke-width='5' stroke-dasharray='6, 14' stroke-linecap='square'/%3E%3C/svg%3E");
      background-position: 50%;
      border-radius: 12px;
      left: -2px;
      top: -2px;
      right: -2px;
      bottom: -2px;
      position: absolute;
    }
  }

  &.droppable .action-box {
    background-color: $colorM10;
  }

  .action-box {
    position: relative;
    padding: 64px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;

    &-number {
      position: absolute;
      top: -18px;
      width: 36px;
      height: 36px;
      background-color: $colorM50;
      color: white;
      font-size: 16px;
      font-weight: 700;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &-img {
      width: 90px;
      height: 90px;
      margin-bottom: 12px;
    }

    .action-box-btn {
      padding: 11px 20px;
      line-height: 18px;
    }

    &-title {
      font-size: 18px;
      font-weight: 700;
      line-height: 22px;
      color: $colorN80;
      margin-bottom: 8px;
    }

    &-text {
      font-size: 13px;
      text-align: center;
      margin-bottom: 16px;
      max-width: 372px;
      line-height: 18px;
      letter-spacing: -0.05px;
    }

    .action-box-text :deep(u) {
      font-weight: 600;
      color: $colorM50;
      text-decoration: none !important;
    }

    &-btn {
      width: auto;
    }

    &-warning {
      margin-top: 16px;
      font-size: 12px;
    }

    .file-info {
      background-color: #fff;
      border: 1px solid #ddd;
      padding: 10px 24px 10px 10px;
      border-radius: 8px;
      margin-bottom: 16px;
      position: relative;
      max-width: 380px;
      width: 100%;

      .icon {
        -webkit-user-drag: none;
      }

      .remove-file {
        position: absolute;
        right: 8px;
        top: 0px;
        font-size: 20px;
        padding: 0px;
      }

      .file-details-container {
        display: flex;
        gap: 16px;
        position: relative;

        img {
          width: 30px;
          height: 40px;
        }

        .file-details {
          width: 100%;
          text-align: left;
          display: flex;
          flex-direction: column;
          gap: 5px;

          .file-name {
            font-size: 13px;
            font-weight: 600;
            line-height: 18px;
            letter-spacing: -0.05px;
            text-overflow: ellipsis;
            overflow: hidden;
            width: 300px;
          }

          .file-size {
            font-size: 14px;
            color: $colorD30;
            margin-top: 4px;
          }
        }
      }

      .file-status {
        margin-top: 12px;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        font-size: 13px;
        gap: 8px;
        color: $colorS50;
        flex-wrap: wrap;

        .file-error-message {
          flex-basis: 100%;
          font-size: 11px;
          line-height: 14px;
          font-weight: 600;
          color: $colorE50;
          margin-top: -2px;
        }

        .warning-message {
          color: $colorE50;
          line-height: 18px;
          font-weight: 600;
          letter-spacing: -0.005px;
        }
        &.error {
          color: $colorE50;
        }

        img {
          width: 16px;
          height: 16px;
          -webkit-user-drag: none;
        }
      }
    }
  }
}
</style>
